import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
// GoogleMapsField import removed - using simple URL input instead

const AdminPartnershipPage = () => {
  const { user } = useUser();
  const [partnerships, setPartnerships] = useState([]);
  const [filteredPartnerships, setFilteredPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [filters, setFilters] = useState({
    category: 'ALL',
    status: 'ALL',
    search: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'displayOrder',
    direction: 'asc'
  });
  const [showViewsInfo, setShowViewsInfo] = useState(false);
  const [showClicksInfo, setShowClicksInfo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Form data for create/edit
  const [formData, setFormData] = useState({
    name: '',
    category: 'BAR',
    description: '',
    logoUrl: '',
    benefitTitle: '',
    benefitDescription: '',
    benefitType: 'DISCOUNT',
    phoneNumber: '',
    websiteUrl: '',
    googleMapsUrl: '',
    operatingHours: '',
    tags: '',
    isActive: true,
    displayOrder: 1
  });

  // Category options
  const categoryOptions = [
    { value: 'BAR', label: 'Bar' },
    { value: 'CAFE', label: 'Cafe' },
    { value: 'RESTAURANT', label: 'Restaurant' },
    { value: 'SHOPPING', label: 'Shopping' },
    { value: 'ENTERTAINMENT', label: 'Entertainment' }
  ];

  // Benefit type options
  const benefitTypeOptions = [
    { value: 'DISCOUNT', label: 'Discount' },
    { value: 'FREE_ITEM', label: 'Free Item' },
    { value: 'SPECIAL_OFFER', label: 'Special Offer' },
    { value: 'MEMBERSHIP', label: 'Membership' }
  ];

  // 권한 체크
  const canManagePartnerships = () => {
    if (!user || !user.role) return false;
    const role = user.role.toUpperCase();
    return ['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(role);
  };

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 데이터 로드
  useEffect(() => {
    fetchPartnerships();
  }, []);

  // 필터링 및 정렬 적용
  useEffect(() => {
    let filtered = partnerships;

    // 카테고리 필터
    if (filters.category !== 'ALL') {
      filtered = filtered.filter(p => p.category === filters.category);
    }

    // 상태 필터
    if (filters.status !== 'ALL') {
      const isActive = filters.status === 'ACTIVE';
      filtered = filtered.filter(p => p.isActive === isActive);
    }

    // 검색 필터
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower) ||
        p.benefitTitle.toLowerCase().includes(searchLower) ||
        (p.tags && p.tags.toLowerCase().includes(searchLower))
      );
    }

    // 정렬
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredPartnerships(filtered);
  }, [partnerships, filters, sortConfig]);

  const fetchPartnerships = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/partnerships');
      setPartnerships(response.data);
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/admin/partnerships', formData);
      
      setShowCreateModal(false);
      resetForm();
      fetchPartnerships();
    } catch (error) {
      // 사용자에게 더 자세한 에러 메시지 표시
      if (error.response?.data?.message) {
        alert(`Create failed: ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        alert('Create failed: Invalid data. Please check all required fields.');
      } else {
        alert('Create failed. Please try again.');
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`/api/admin/partnerships/${selectedPartnership.id}`, formData);
      
      setShowEditModal(false);
      resetForm();
      fetchPartnerships();
    } catch (error) {
      // 사용자에게 더 자세한 에러 메시지 표시
      if (error.response?.data?.message) {
        alert(`Update failed: ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        alert('Update failed: Invalid data. Please check all required fields.');
      } else {
        alert('Update failed. Please try again.');
      }
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/admin/partnerships/${selectedPartnership.id}`);
      setShowDeleteModal(false);
      fetchPartnerships();
    } catch (error) {
      alert('Failed to delete partnership');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'BAR',
      description: '',
      logoUrl: '',
      benefitTitle: '',
      benefitDescription: '',
      benefitType: 'DISCOUNT',
      phoneNumber: '',
      websiteUrl: '',
      googleMapsUrl: '',
      operatingHours: '',
      tags: '',
      isActive: true,
      displayOrder: 1
    });
    setSelectedPartnership(null);
  };

  // Google Maps data extraction removed - using simple URL input instead


  const openEditModal = (partnership) => {
    setSelectedPartnership(partnership);
    setFormData({
      name: partnership.name,
      category: partnership.category,
      description: partnership.description,
      logoUrl: partnership.logoUrl,
      benefitTitle: partnership.benefitTitle,
      benefitDescription: partnership.benefitDescription,
      benefitType: partnership.benefitType,
      phoneNumber: partnership.phoneNumber,
      websiteUrl: partnership.websiteUrl,
      googleMapsUrl: partnership.googleMapsUrl || '',
      operatingHours: partnership.operatingHours,
      tags: partnership.tags,
      isActive: partnership.isActive,
      displayOrder: partnership.displayOrder
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (partnership) => {
    setSelectedPartnership(partnership);
    setShowDeleteModal(true);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (!canManagePartnerships()) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to manage partnerships.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Partnership Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage partnership information and benefits</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium shadow-sm"
        >
          Add New Partnership
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Categories</option>
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by name, description, or tags..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-2xl font-bold text-blue-600">{partnerships.length}</div>
          <div className="text-sm text-gray-600">Total Partnerships</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-2xl font-bold text-green-600">
            {partnerships.filter(p => p.isActive).length}
          </div>
          <div className="text-sm text-gray-600">Active Partnerships</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-2xl font-bold text-purple-600">
            {partnerships.reduce((sum, p) => sum + (p.viewCount || 0), 0)}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-1">
            Total Views
            <button
              onClick={() => setShowViewsInfo(!showViewsInfo)}
              className="text-gray-400 hover:text-gray-600 text-xs"
              title="Click for details"
            >
              ℹ️
            </button>
          </div>
          {showViewsInfo && (
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-800">
              <strong>Views:</strong> Desktop: Mouse hover + Click | Mobile: Click only
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="text-2xl font-bold text-orange-600">
            {partnerships.reduce((sum, p) => sum + (p.clickCount || 0), 0)}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-1">
            Total Clicks
            <button
              onClick={() => setShowClicksInfo(!showClicksInfo)}
              className="text-gray-400 hover:text-gray-600 text-xs"
              title="Click for details"
            >
              ℹ️
            </button>
          </div>
          {showClicksInfo && (
            <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-orange-800">
              <strong>Clicks:</strong> "Visit Website" button clicks only
            </div>
          )}
        </div>
      </div>

      {/* Partnerships Table */}
      {isMobile ? (
        // Mobile Card View
        <div className="space-y-4">
          {filteredPartnerships.map((partnership) => (
            <div key={partnership.id} className="bg-white p-4 rounded-lg shadow-md border">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <img
                    src={partnership.logoUrl}
                    alt={partnership.name}
                    className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => {
                      e.target.src = '/placeholder-logo.png';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{partnership.name}</h3>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        partnership.category === 'BAR' ? 'bg-purple-100 text-purple-800' :
                        partnership.category === 'CAFE' ? 'bg-yellow-100 text-yellow-800' :
                        partnership.category === 'RESTAURANT' ? 'bg-red-100 text-red-800' :
                        partnership.category === 'SHOPPING' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {partnership.category}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        partnership.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {partnership.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Benefit:</span> {partnership.benefitTitle}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Description:</span> {partnership.description}
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Views:</span>
                    <span className="ml-1 font-medium">{partnership.viewCount || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Clicks:</span>
                    <span className="ml-1 font-medium">{partnership.clickCount || 0}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openEditModal(partnership)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteModal(partnership)}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredPartnerships.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No partnerships found</p>
            </div>
          )}
        </div>
      ) : (
        // Desktop Table View
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-16 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('displayOrder')}>
                  Order
                  {sortConfig.key === 'displayOrder' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="w-80 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}>
                  Name
                  {sortConfig.key === 'name' && (
                    <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="w-24 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="w-64 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Benefit
                </th>
                <th className="w-20 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-32 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statistics
                </th>
                <th className="w-24 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPartnerships.map((partnership) => (
                <tr key={partnership.id} className="hover:bg-gray-50">
                  <td className="w-16 px-3 py-4 text-center text-sm text-gray-900">
                    {partnership.displayOrder || '-'}
                  </td>
                  <td className="w-80 px-3 py-4">
                    <div className="flex items-center">
                      <img
                        src={partnership.logoUrl}
                        alt={partnership.name}
                        className="h-12 w-12 rounded-lg object-cover mr-3 flex-shrink-0"
                        onError={(e) => {
                          e.target.src = '/placeholder-logo.png';
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{partnership.name}</div>
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2" title={partnership.description}>
                          {partnership.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="w-24 px-3 py-4 text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {partnership.category}
                    </span>
                  </td>
                  <td className="w-64 px-3 py-4">
                    <div className="text-sm font-medium text-gray-900 truncate" title={partnership.benefitTitle}>
                      {partnership.benefitTitle}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-line" title={partnership.benefitDescription}>
                      {partnership.benefitDescription}
                    </div>
                  </td>
                  <td className="w-20 px-3 py-4 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      partnership.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {partnership.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="w-32 px-3 py-4 text-center text-xs text-gray-500">
                    <div className="space-y-1">
                      <div>Views: {partnership.viewCount || 0}</div>
                      <div>Clicks: {partnership.clickCount || 0}</div>
                    </div>
                  </td>
                  <td className="w-24 px-3 py-4 text-center">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => openEditModal(partnership)}
                        className="text-blue-600 hover:text-blue-900 text-xs"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteModal(partnership)}
                        className="text-red-600 hover:text-red-900 text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Create New Partnership</h2>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    rows={3}
                    maxLength={1000}
                    placeholder="e.g., A cozy neighborhood cafe offering fresh coffee beans and handmade desserts"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Brief description of the partnership
                    </p>
                    <p className={`text-xs ${formData.description.length > 900 ? 'text-red-500' : formData.description.length > 800 ? 'text-yellow-500' : 'text-gray-500'}`}>
                      {formData.description.length}/1000
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL *</label>
                  <input
                    type="url"
                    required
                    value={formData.logoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const maxOrder = Math.max(...partnerships.map(p => p.displayOrder || 0), 0);
                        setFormData(prev => ({ ...prev, displayOrder: maxOrder + 1 }));
                      }}
                      className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Last
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Lower numbers appear first (1, 2, 3...). Use "Last" to add at the end.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Title *</label>
                  <textarea
                    required
                    rows={2}
                    placeholder="e.g., 10% discount for SLAM members (you can use line breaks)"
                    value={formData.benefitTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, benefitTitle: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use line breaks for clearer titles</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Type *</label>
                  <select
                    required
                    value={formData.benefitType}
                    onChange={(e) => setFormData(prev => ({ ...prev, benefitType: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {benefitTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Description *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <textarea
                      required
                      rows={3}
                      maxLength={1000}
                      placeholder="e.g., Show your SLAM membership card to get 10% discount on all menu items (excluding alcoholic beverages)"
                      value={formData.benefitDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, benefitDescription: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="font-medium text-gray-700 mb-1">💡 <strong>Writing tip</strong></p>
                      <p>
                        On the partnership card, content is <strong>center-aligned</strong>. We recommend adding <strong>line breaks</strong> before filling a whole line to keep the layout <strong>clean and readable</strong>.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">Detailed description of the benefit</p>
                    <p className={`${formData.benefitDescription.length > 900 ? 'text-red-500' : formData.benefitDescription.length > 800 ? 'text-yellow-500' : 'text-gray-500'} text-xs`}>
                      {formData.benefitDescription.length}/1000
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Representative URL</label>
                  <input
                    type="url"
                    placeholder="e.g., https://www.instagram.com/cafe_name or https://www.cafe.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, websiteUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Operating Hours</label>
                  <textarea
                    rows={3}
                    placeholder="e.g., Mon-Sun: 6PM-2AM"
                    value={formData.operatingHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, operatingHours: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use line breaks for different days
                  </p>
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="e.g., atmosphere, team-friendly, student-discount"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate tags with commas or # (e.g., atmosphere, #team-friendly, student-discount)
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Partnership
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Edit Partnership</h2>
                  <p className="text-sm text-gray-600 mt-1">Update partnership information and benefits</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
            <form onSubmit={handleUpdate}>
              {/* Same form fields as create modal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categoryOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    rows={3}
                    maxLength={1000}
                    placeholder="e.g., A cozy neighborhood cafe offering fresh coffee beans and handmade desserts"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Brief description of the partnership
                    </p>
                    <p className={`text-xs ${formData.description.length > 900 ? 'text-red-500' : formData.description.length > 800 ? 'text-yellow-500' : 'text-gray-500'}`}>
                      {formData.description.length}/1000
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL *</label>
                  <input
                    type="url"
                    required
                    value={formData.logoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, logoUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 1 }))}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const maxOrder = Math.max(...partnerships.map(p => p.displayOrder || 0), 0);
                        setFormData(prev => ({ ...prev, displayOrder: maxOrder + 1 }));
                      }}
                      className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Last
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Lower numbers appear first (1, 2, 3...). Use "Last" to add at the end.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Title *</label>
                  <textarea
                    required
                    rows={2}
                    placeholder="e.g., 10% discount for SLAM members (you can use line breaks)"
                    value={formData.benefitTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, benefitTitle: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use line breaks for clearer titles</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Type *</label>
                  <select
                    required
                    value={formData.benefitType}
                    onChange={(e) => setFormData(prev => ({ ...prev, benefitType: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {benefitTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Description *</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <textarea
                      required
                      rows={3}
                      maxLength={1000}
                      placeholder="e.g., Show your SLAM membership card to get 10% discount on all menu items (excluding alcoholic beverages)"
                      value={formData.benefitDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, benefitDescription: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="font-medium text-gray-700 mb-1">💡 <strong>Writing tip</strong></p>
                      <p>
                        On the partnership card, content is <strong>center-aligned</strong>. We recommend adding <strong>line breaks</strong> before filling a whole line to keep the layout <strong>clean and readable</strong>.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">Detailed description of the benefit</p>
                    <p className={`${formData.benefitDescription.length > 900 ? 'text-red-500' : formData.benefitDescription.length > 800 ? 'text-yellow-500' : 'text-gray-500'} text-xs`}>
                      {formData.benefitDescription.length}/1000
                    </p>
                  </div>
                </div>
                {/* Google Maps URL */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Maps URL (Optional)
                  </label>
                  <input
                    type="url"
                    placeholder="https://maps.google.com/... or https://goo.gl/maps/..."
                    value={formData.googleMapsUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, googleMapsUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter Google Maps link for location reference
                  </p>
                  {formData.googleMapsUrl && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => window.open(formData.googleMapsUrl, '_blank')}
                        className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded hover:bg-green-200"
                      >
                        Open in Google Maps
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Representative URL</label>
                  <input
                    type="url"
                    placeholder="e.g., https://www.instagram.com/cafe_name or https://www.cafe.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, websiteUrl: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Operating Hours</label>
                  <textarea
                    rows={3}
                    placeholder="e.g., Mon-Sun: 6PM-2AM"
                    value={formData.operatingHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, operatingHours: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use line breaks for different days
                  </p>
                </div>
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    placeholder="e.g., atmosphere, team-friendly, student-discount"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separate tags with commas or # (e.g., atmosphere, #team-friendly, student-discount)
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                >
                  Update Partnership
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Delete Partnership</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedPartnership?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartnershipPage;
